import React, { useEffect, useRef, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppSelector } from "../../../../redux/hook";
import { useExportEmployeesWage } from "../../../../backend/exports";
import { downloadFileContent } from "../../../../utils/common";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { toast } from "react-toastify";
import { getEmployeesWage } from "../../../../redux/features/performerClient.slice";
import EmployeeUploadFilters from "../shared/EmployeeUploadFilters";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import AdditionalFiltersInputUploads from "../shared/AdditionalFiltersInputUploads";
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  usePerformerClientCompanyData,
  useEmployeeUploadCompanyAutoSelect,
} from "../../../../hooks/useEmployeeUploads";
import {
  formatEmployeeUploadDate,
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload,
} from "../../../../utils/employeeUploads.utils";
import { UploadButton } from "../shared";
import PerformerTable from "../shared/PerformerTable";
import "../shared/performer-client.css";
import Icon from "../../../common/Icon";


const EmployeeWageUpload = () => {

  // Redux selectors
  const employeesWageDetails = useAppSelector(
    (state) => state.performerClient.employeesWageDetails,
  );

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } =
    useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig({
    activeSort: "created_at",
    sortType: "desc",
  });
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: "month_eq",
    searchFieldName: "employee_code_cont",
  });
  const {
    companies,
    associateCompanies,
    locations,
    companiesLoading,
    associateCompaniesLoading,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
  } = usePerformerClientCompanyData();

  // Establishment Type rests at "All" (blank) on this page -- only Company
  // auto-selects, first alphabetically, once the list resolves. Also keeps
  // the navbar/sidenav branding in sync with the resolved company/AC
  // (bindLogo defaults to true).
  useEmployeeUploadCompanyAutoSelect(filters, updateFilter, companies, companiesLoading, associateCompanies, associateCompaniesLoading);

  // Local state for UI components
  const [companyName, setCompanyName] = useState("");
  const [associate_company_name, setAssociateCompanyName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState("");
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] =
    useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] =
    useState<any>({});

  const employeesWage = employeesWageDetails.data.items || [];
  const employeesWageCount = employeesWageDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesWage(
    (response: any) => {
      let filename =
        associate_company_name +
        "_" +
        locationName +
        "_" +
        "Employee-Wage" +
        "_" +
        filters.year +
        "_" +
        filters.month +
        ".xlsx";
      toast.success('Export will be sent to your registered email.');
    },
    () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
    },
  );

  const loading = exporting || employeesWageDetails.status === "loading";

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployeesWage, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters)
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesWage, filters, { ...searchConfig, page: 0 });
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
    filters.year,
    filters.month,
    filters.employer,
    filters.employerLocation,
    filters.subContractor,
    filters.subContractorLocation,
  ]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(
        filters.company,
        companies,
      );
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(
        filters.associateCompany,
        associateCompanies,
      );
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split("^")[0];
      const selectedLocationName = getEmployeeUploadLocationById(
        selectedLocationId,
        locations,
      );
      setLocationName(selectedLocationName);
      const tempLocation =
        locations.find((loc: any) => loc.location_id === selectedLocationId) ||
        {};
      setCompanyLocationMappingId(tempLocation.company_location_mapping_id || "");
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
  ]);

  // Handler functions
  const handleSearch = () => {
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesWage, filters, { ...searchConfig, page: 0 });
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: "" });
    fetchData(getEmployeesWage, filters, { ...searchConfig, searchInput: "" });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection =
      searchConfig.activeSort === sortField && searchConfig.sortType === "asc"
        ? "desc"
        : "asc";
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection,
    });
    fetchData(
      getEmployeesWage,
      filters,
      searchConfig,
      {},
      sortField,
      newSortDirection,
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesWage, filters, { ...searchConfig, page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
    fetchData(getEmployeesWage, filters, {
      ...searchConfig,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || "",
      employerLocation: filterData.payload.employer_location_id_eq || "",
      subContractor: filterData.payload.sub_contractor_id_eq || "",
      subContractorLocation:
        filterData.payload.sub_contractor_location_id_eq || "",
    });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      { monthFieldName: "month_eq", searchFieldName: "employee_code_cont" },
    );
    exportRecords(payload);
  };

  useEffect(() => {
    if (employeesWageDetails.status === "succeeded") {
    } else if (employeesWageDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesWageDetails.status]);

  const formatDate = formatEmployeeUploadDate;

  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
        <div style={{ height: "100%" }}>
          <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 className="mb-0">Employee Wage</h4>
            <EmployeeUploadSearch
              value={searchConfig.searchInput}
              placeholder="Search (Employee Code)"
              onChange={(e) => updateSearchConfig({ searchInput: e.target.value })}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          <div className="filters" style={{ padding: "16px 20px" }}>
            <div style={{ backgroundColor: "#ffffff", padding: "16px 16px", borderRadius: "8px" }}>
              <EmployeeUploadFilters
                filters={filters}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                fetchAssociateCompanies={fetchAssociateCompanies}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (
                    filters.company &&
                    filters.associateCompany &&
                    filters.location
                  ) {
                    setShowAdditionalFiltersModal(true);
                  } else {
                    toast.error(
                      "Please select Company, Associate Company and Location to apply additional filters",
                    );
                  }
                }}
                actions={
                  <>
                    <div style={{ cursor: (!filters.company || !filters.associateCompany) ? "not-allowed" : "pointer" }}>
                      <button
                        onClick={onClickExport}
                        disabled={!filters.company || !filters.associateCompany}
                        className="download-btn"
                        title='Export Data'
                      >
                        <Icon name="downloadIcon" />
                      </button>
                    </div>
                    <UploadButton
                      configType="Employee Wage"
                      filters={{
                        company: filters.company,
                        associateCompany: filters.associateCompany,
                        establishmentType: filters.establishmentType,
                        location: filters.location ? filters.location.split("^")[0] : "",
                      }}
                      selectedAdditionalFilters={selectedAdditionalFilters}
                      onUploadSuccess={() =>
                        fetchData(getEmployeesWage, filters, searchConfig)
                      }
                    />
                  </>
                }
              />
            </div>
          </div>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId,
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <div style={{ padding: "0 20px" }}>
            <PerformerTable
              columns={[
                { label: "Employee Code", sortKey: "employee_code", render: (row) => row.employee_code },
                ...(filters.establishmentType === "CLRA" ? [{ label: "Company Employer Sub-Contractor Code", render: (row: any) => (row.company_employer_sub_contractor || {}).company_employer_sub_contractor_code }] : []),
                { label: "Month", sortKey: "no_of_days", render: (row) => row.month },
                { label: "Present Days", render: (row) => row.present_days },
                { label: "WorkDays", render: (row) => row.work_days },
                { label: "Basic Wages", render: (row) => row.basic_wages },
                { label: "Basic Arrear", render: (row) => row.basic_arrear },
                { label: "Fixed Gross", render: (row) => row.fixed_gross },
                { label: "Gross Deduction", render: (row) => row.gross_deduction },
                { label: "Gross Ded Arrear", render: (row) => row.gross_deduction_arrear },
                { label: "DA", render: (row) => row.dearness_allowance },
                { label: "DA Arrear", render: (row) => row.dearness_arrear },
                { label: "Gross Earnings", render: (row) => row.gross_earnings },
                { label: "Gross Earnings Arrear", render: (row) => row.gross_earnings_arrear },
                { label: "Date Of Payment", render: (row) => formatDate(row.date_of_payment) },
                { label: "ESi", render: (row) => row.esi },
                { label: "ESi Arrear", render: (row) => row.esi_arrear },
                { label: "Esi Date Of Payment", render: (row) => formatDate(row.esi_date_of_payment) },
                { label: "Esi Date of Notice", render: (row) => formatDate(row.esi_date_of_notice) },
                { label: "ESi Time of Notice", render: (row) => row.esi_time_of_notice },
                { label: "Esi Cause", render: (row) => row.esi_cause },
                { label: "Esi Nature", render: (row) => row.esi_nature },
                { label: "Esi Date", render: (row) => formatDate(row.esi_date) },
                { label: "Esi Time", render: (row) => row.esi_time },
                { label: "Esi Place", render: (row) => row.esi_place },
                { label: "Conveyance Allo.", render: (row) => row.conveyance_allowance },
                { label: "Conveyance Arrear", render: (row) => row.conveyance_arreat },
                { label: "HRA", sortKey: "house_rent_allowance", render: (row) => row.house_rent_allowance },
                { label: "HRA Arrear", render: (row) => row.house_rent_arrear },
                { label: "Spl Allow", render: (row) => row.special_allowance },
                { label: "Spl Allow Arrear", render: (row) => row.special_arrear },
                { label: "Insurance", render: (row) => row.insurance },
                { label: "Income Tax", render: (row) => row.income_tax },
                { label: "Fines", render: (row) => row.fines },
                { label: "Bonus", render: (row) => row.bonus },
                { label: "OverTime", render: (row) => row.overtime },
                { label: "OverTime Hrs", render: (row) => row.overtime_hours },
                { label: "ProvidentFund", render: (row) => row.provident_fund },
                { label: "Pf Arrear", render: (row) => row.provident_fund_arrear },
                { label: "Professional Tax", render: (row) => row.professional_tax },
                { label: "Prof Tax Arrear", render: (row) => row.profession_tax_arrear },
                { label: "Medical Allowance", render: (row) => row.medical_allowance },
                { label: "Net Payable", render: (row) => row.net_payable },
                { label: "Net Payable Arrear", render: (row) => row.net_payable_arrear },
                { label: "OverTime Taken Date", render: (row) => row.over_time_taken_date },
                { label: "Maternity Leave From Date", render: (row) => formatDate(row.maternity_leave_from_date) },
                { label: "DOB Of Child", render: (row) => formatDate(row.date_of_birth_ofchild) },
                { label: "Maternity Paid Date", render: (row) => formatDate(row.maternity_paid_date) },
                { label: "Maternity Advance Paid", render: (row) => row.maternity_advance_paid },
                { label: "Maternity Bonus", render: (row) => row.maternity_bonus },
                { label: "Cause Of Damage or Loss Date", render: (row) => formatDate(row.cause_of_damage_or_loss_date) },
                { label: "Date Of Deduction Show Cause", render: (row) => formatDate(row.date_of_deduction_show_cause) },
                { label: "Damage Loss Amount", render: (row) => row.damage_loss_amount },
                { label: "No.Of Ded Installments", render: (row) => row.noof_deduction_installment_to_be_recovered },
                { label: "Date On which Ded Competed", render: (row) => formatDate(row.dateon_which_deduction_completed) },
                { label: "Salary Adv Paid Date", render: (row) => formatDate(row.advance_paid_date) },
                { label: "Salary Advance", render: (row) => row.salary_advance },
                { label: "No.Of Adv Installments to be recovered", render: (row) => row.no_of_adv_installments_to_be_recovered },
                { label: "Date on Adv Recovery Completed", render: (row) => formatDate(row.date_on_adv_recovery_completed) },
                { label: "Nature & Date for Fines", render: (row) => row.nature_date_for_fine },
              ]}
              data={employeesWage}
              count={employeesWageCount}
              page={searchConfig.page}
              rowsPerPage={searchConfig.rowsPerPage}
              activeSort={searchConfig.activeSort}
              sortType={searchConfig.sortType}
              onSort={handleSort}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    </>
  );
};

export default EmployeeWageUpload;
